import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/hooks/use-auth";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader2, Eye, EyeOff, Mail } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuery } from "@tanstack/react-query";
import speiseGuruLogoSvg from "@/assets/speiseguru_logo.svg";

// Login-Formular-Schema
const loginSchema = z.object({
  username: z.string().min(3, { message: "Benutzername muss mindestens 3 Zeichen haben" }),
  password: z.string().min(6, { message: "Passwort muss mindestens 6 Zeichen haben" }),
  rememberMe: z.boolean().optional()
});

// Registrierungs-Formular-Schema
const registerSchema = z.object({
  email: z.string().email({ message: "Ungültiges E-Mail-Format" }),
  firstName: z.string().min(2, { message: "Vorname muss mindestens 2 Zeichen haben" }),
  lastName: z.string().min(2, { message: "Nachname muss mindestens 2 Zeichen haben" }),
  password: z.string().min(6, { message: "Passwort muss mindestens 6 Zeichen haben" }),
  confirmPassword: z.string()
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwörter stimmen nicht überein",
  path: ["confirmPassword"]
});

type LoginFormValues = z.infer<typeof loginSchema>;
type RegisterFormValues = z.infer<typeof registerSchema>;

// Google SVG icon
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

// Facebook SVG icon
function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#1877F2">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );
}

// hCaptcha widget
function HCaptchaWidget({ siteKey, onVerify, onExpire }: {
  siteKey: string;
  onVerify: (token: string) => void;
  onExpire: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!(window as any).hcaptcha) {
      const script = document.createElement('script');
      script.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
      script.async = true;
      script.onload = () => renderCaptcha();
      document.head.appendChild(script);
    } else {
      renderCaptcha();
    }

    function renderCaptcha() {
      if (containerRef.current && (window as any).hcaptcha) {
        setTimeout(() => {
          try {
            widgetIdRef.current = (window as any).hcaptcha.render(containerRef.current, {
              sitekey: siteKey,
              callback: onVerify,
              'expired-callback': onExpire,
              theme: 'light',
              size: 'normal',
            });
          } catch (e) {
            console.error('hCaptcha render error:', e);
          }
        }, 100);
      }
    }

    return () => {
      if (widgetIdRef.current !== null && (window as any).hcaptcha) {
        try { (window as any).hcaptcha.reset(widgetIdRef.current); } catch {}
      }
    };
  }, [siteKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div ref={containerRef} className="flex justify-center" />;
}

export default function AuthPage() {
  const [activeMode, setActiveMode] = useState<'login' | 'register'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [_, setLocation] = useLocation();
  const { user, isLoading, loginMutation, registerMutation } = useAuth();

  // Fetch available auth providers
  const { data: providers } = useQuery<{
    google: boolean;
    facebook: boolean;
    hcaptchaSiteKey: string | null;
  }>({
    queryKey: ["/api/auth/providers"],
    staleTime: 60 * 60 * 1000,
  });

  useEffect(() => {
    if (user) setLocation("/");
  }, [user, setLocation]);

  // Check for OAuth error in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error')) setActiveMode('register');
  }, []);

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: "", password: "", rememberMe: false }
  });

  const registerForm = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { email: "", firstName: "", lastName: "", password: "", confirmPassword: "" }
  });

  const onLoginSubmit = (data: LoginFormValues) => {
    loginMutation.mutate(data);
  };

  const onRegisterSubmit = (data: RegisterFormValues) => {
    const { confirmPassword, ...userData } = data;
    registerMutation.mutate({
      ...userData,
      username: userData.email,
      captchaToken: captchaToken || undefined,
    });
  };

  const handleCaptchaVerify = useCallback((token: string) => setCaptchaToken(token), []);
  const handleCaptchaExpire = useCallback(() => setCaptchaToken(null), []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F8FAF9]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0E7F4F]" />
      </div>
    );
  }

  const googleEnabled = providers?.google;
  const facebookEnabled = providers?.facebook;
  const hcaptchaSiteKey = providers?.hcaptchaSiteKey;

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #F5F7F6 0%, #E8F0EC 100%)' }}>
      {/* Background Pattern */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full" style={{ background: 'radial-gradient(ellipse 80% 60% at 20% 20%, rgba(14,127,79,0.12), transparent 60%)' }} />
        <div className="absolute top-0 left-0 w-full h-full" style={{ background: 'radial-gradient(ellipse 70% 50% at 85% 80%, rgba(14,127,79,0.08), transparent 50%)' }} />
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-16">
        {/* Header */}
        <div className="text-center mb-8">
          <Link href="/">
            <img src={speiseGuruLogoSvg} alt="SpeiseGuru Logo" className="h-10 w-auto mx-auto mb-6 hover:opacity-80 transition-opacity cursor-pointer" />
          </Link>
          {activeMode === 'login' ? (
            <>
              <h1 className="text-2xl md:text-3xl font-semibold text-[#1C1C1E] mb-2">Willkommen zurück 👋</h1>
              <p className="text-sm text-gray-500">Schön dich wiederzusehen.</p>
            </>
          ) : (
            <>
              <h1 className="text-2xl md:text-3xl font-semibold text-[#1C1C1E] mb-2">Konto erstellen</h1>
              <p className="text-sm text-gray-500">Starte deine kulinarische Entdeckungsreise.</p>
            </>
          )}
        </div>

        {/* Auth Card */}
        <div className="w-full max-w-[440px] rounded-2xl bg-white/95 backdrop-blur-sm overflow-hidden" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)' }}>
          <div className="px-7 py-9 sm:px-9 sm:py-10">

            {/* Social Login Buttons — always visible */}
            <div className="space-y-3 mb-6">
              {googleEnabled ? (
                <a
                  href="/api/auth/google"
                  className="group flex items-center gap-3 w-full h-[52px] px-5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 text-[#374151] font-medium text-[14px] transition-all duration-200"
                >
                  <GoogleIcon className="w-5 h-5 shrink-0" />
                  <span className="flex-1 text-center">Mit Google {activeMode === 'login' ? 'anmelden' : 'registrieren'}</span>
                </a>
              ) : (
                <div
                  className="group flex items-center gap-3 w-full h-[52px] px-5 rounded-xl border border-gray-100 bg-gray-50/60 text-gray-400 font-medium text-[14px] select-none"
                >
                  <GoogleIcon className="w-5 h-5 shrink-0 opacity-40" />
                  <span className="flex-1 text-center">Mit Google {activeMode === 'login' ? 'anmelden' : 'registrieren'}</span>
                  <span className="shrink-0 text-[10px] tracking-wide uppercase text-gray-300 font-semibold bg-gray-100 px-2 py-0.5 rounded-full">Bald</span>
                </div>
              )}
              {facebookEnabled ? (
                <a
                  href="/api/auth/facebook"
                  className="group flex items-center gap-3 w-full h-[52px] px-5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 text-[#374151] font-medium text-[14px] transition-all duration-200"
                >
                  <FacebookIcon className="w-5 h-5 shrink-0" />
                  <span className="flex-1 text-center">Mit Facebook {activeMode === 'login' ? 'anmelden' : 'registrieren'}</span>
                </a>
              ) : (
                <div
                  className="group flex items-center gap-3 w-full h-[52px] px-5 rounded-xl border border-gray-100 bg-gray-50/60 text-gray-400 font-medium text-[14px] select-none"
                >
                  <FacebookIcon className="w-5 h-5 shrink-0 opacity-40" />
                  <span className="flex-1 text-center">Mit Facebook {activeMode === 'login' ? 'anmelden' : 'registrieren'}</span>
                  <span className="shrink-0 text-[10px] tracking-wide uppercase text-gray-300 font-semibold bg-gray-100 px-2 py-0.5 rounded-full">Bald</span>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-4 text-gray-400 font-medium tracking-wide">oder mit E-Mail</span>
              </div>
            </div>

            {activeMode === 'login' ? (
              <Form {...loginForm}>
                <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-5">
                  <FormField control={loginForm.control} name="username" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-[#374151]">E-Mail / Benutzername</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
                          <Input placeholder="email@example.com" {...field} className="h-12 pl-10 rounded-xl border-[#E5E7EB] focus:border-[#0E7F4F] focus:ring-[#0E7F4F]/20 transition-colors duration-200" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField control={loginForm.control} name="password" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-[#374151]">Passwort</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input type={showPassword ? "text" : "password"} placeholder="••••••••" {...field} className="h-12 rounded-xl border-[#E5E7EB] focus:border-[#0E7F4F] focus:ring-[#0E7F4F]/20 pr-12 transition-colors duration-200" />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center justify-center w-12 text-[#6B7280] hover:text-[#0E7F4F] transition-colors">
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <div className="flex items-center justify-between">
                    <FormField control={loginForm.control} name="rememberMe" render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} className="border-[#E5E7EB] data-[state=checked]:bg-[#0E7F4F] data-[state=checked]:border-[#0E7F4F]" />
                        </FormControl>
                        <FormLabel className="text-sm text-[#6B7280] font-normal cursor-pointer">Angemeldet bleiben</FormLabel>
                      </FormItem>
                    )} />
                    <button type="button" className="text-sm text-[#0E7F4F] hover:text-[#0D6F43] font-medium transition-colors">Passwort vergessen?</button>
                  </div>

                  <div className="pt-2">
                    <Button type="submit" className="w-full h-12 rounded-xl bg-[#0E7F4F] hover:bg-[#0D6F43] active:scale-[0.98] text-white font-semibold shadow-sm hover:shadow transition-colors duration-200 disabled:opacity-50" disabled={loginMutation.isPending}>
                      {loginMutation.isPending ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Bitte warten...</>) : 'Anmelden'}
                    </Button>
                  </div>

                  {loginMutation.error && (
                    <div className="p-3 rounded-xl bg-red-50 border border-red-100">
                      <p className="text-sm text-red-600">{loginMutation.error.message || 'Anmeldung fehlgeschlagen'}</p>
                    </div>
                  )}
                </form>
              </Form>
            ) : (
              <Form {...registerForm}>
                <form onSubmit={registerForm.handleSubmit(onRegisterSubmit)} className="space-y-5">
                  <FormField control={registerForm.control} name="email" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-[#374151]">E-Mail</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
                          <Input placeholder="email@example.com" {...field} className="h-12 pl-10 rounded-xl border-[#E5E7EB] focus:border-[#0E7F4F] focus:ring-[#0E7F4F]/20 transition-colors duration-200" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={registerForm.control} name="firstName" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-[#374151]">Vorname</FormLabel>
                        <FormControl><Input placeholder="Vorname" {...field} className="h-12 rounded-xl border-[#E5E7EB] focus:border-[#0E7F4F] focus:ring-[#0E7F4F]/20 transition-colors duration-200" /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={registerForm.control} name="lastName" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-[#374151]">Nachname</FormLabel>
                        <FormControl><Input placeholder="Nachname" {...field} className="h-12 rounded-xl border-[#E5E7EB] focus:border-[#0E7F4F] focus:ring-[#0E7F4F]/20 transition-colors duration-200" /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  <FormField control={registerForm.control} name="password" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-[#374151]">Passwort</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input type={showPassword ? "text" : "password"} placeholder="••••••••" {...field} className="h-12 rounded-xl border-[#E5E7EB] focus:border-[#0E7F4F] focus:ring-[#0E7F4F]/20 pr-12 transition-colors duration-200" />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center justify-center w-12 text-[#6B7280] hover:text-[#0E7F4F] transition-colors">
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField control={registerForm.control} name="confirmPassword" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-[#374151]">Passwort bestätigen</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input type={showConfirmPassword ? "text" : "password"} placeholder="••••••••" {...field} className="h-12 rounded-xl border-[#E5E7EB] focus:border-[#0E7F4F] focus:ring-[#0E7F4F]/20 pr-12 transition-colors duration-200" />
                          <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 flex items-center justify-center w-12 text-[#6B7280] hover:text-[#0E7F4F] transition-colors">
                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  {/* hCaptcha */}
                  {hcaptchaSiteKey && (
                    <div className="pt-1">
                      <HCaptchaWidget siteKey={hcaptchaSiteKey} onVerify={handleCaptchaVerify} onExpire={handleCaptchaExpire} />
                    </div>
                  )}

                  <div className="pt-2">
                    <Button type="submit" className="w-full h-12 rounded-xl bg-[#0E7F4F] hover:bg-[#0D6F43] active:scale-[0.98] text-white font-semibold shadow-sm hover:shadow transition-colors duration-200 disabled:opacity-50" disabled={registerMutation.isPending || (!!hcaptchaSiteKey && !captchaToken)}>
                      {registerMutation.isPending ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Bitte warten...</>) : 'Konto erstellen'}
                    </Button>
                  </div>

                  {registerMutation.error && (
                    <div className="p-3 rounded-xl bg-red-50 border border-red-100">
                      <p className="text-sm text-red-600">{registerMutation.error.message || 'Registrierung fehlgeschlagen'}</p>
                    </div>
                  )}
                </form>
              </Form>
            )}

            {/* Mode Switch */}
            <div className="mt-8 pt-6 border-t border-gray-100 text-center">
              {activeMode === 'login' ? (
                <p className="text-sm text-[#6B7280]">
                  Noch kein Konto?{' '}
                  <button onClick={() => setActiveMode('register')} className="text-[#0E7F4F] hover:text-[#0D6F43] font-semibold transition-colors">Registrieren</button>
                </p>
              ) : (
                <p className="text-sm text-[#6B7280]">
                  Bereits registriert?{' '}
                  <button onClick={() => setActiveMode('login')} className="text-[#0E7F4F] hover:text-[#0D6F43] font-semibold transition-colors">Anmelden</button>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Legal note */}
        <p className="mt-6 text-xs text-center text-gray-400 max-w-sm">
          Mit der Anmeldung akzeptierst du unsere{' '}
          <Link href="/datenschutz" className="underline hover:text-gray-600">Datenschutzrichtlinie</Link>
          {' '}und{' '}
          <Link href="/agb" className="underline hover:text-gray-600">Nutzungsbedingungen</Link>.
        </p>
      </div>
    </div>
  );
}
